import React, { useEffect, useMemo, useState } from "react";
import HeatmapChart from "../../CHARTS/HeatMapChart";
import NewHeatMapChart from "../../CHARTS/NewHeatMapChart";
const keyMap = {
  "≤4 hrs": "wh_less_than_equals_4hr",
  "4–6 hrs": "wh_4_to_6_hr",
  "6–8 hrs": "wh_6_to_8_hr",
  "8–10 hrs": "wh_8_to_9_hr sum wh_9_to10_hr",
  "10–12 hrs": "wh_10_to_11_hr sum wh_11_to_12_hr",
  "12–14 hrs": "wh_12_to_14_hr",
  "≥14 hrs": "wh_more_than_14hr",
};
const HOUR_LABELS = [
  "≤4 hrs",
  "4–6 hrs",
  "6–8 hrs",
  "8–10 hrs",
  "10–12 hrs",
  "12–14 hrs",
  "≥14 hrs",
];

const colors = ["#edf8fb", "#b2e2e2", "#66c2a4", "#2ca25f", "#006d2c"];
const toNum = (v) => (v != null && !isNaN(v) ? Number(v) : 0);

const MonthLegend = ({ months = [], selectedMonths = [], onClickMonth }) => {
  const list = Array.isArray(months) ? months : [];
  const selected = Array.isArray(selectedMonths) ? selectedMonths : [];
  return (
    <div className="d-flex">
      {list.map((month) => {
        const isSelected = selected.includes(month);
        return (
          <div
            key={month}
            onClick={() => onClickMonth(month)}
            style={{
              padding: "4px 10px",
              margin: "2px",
              fontSize: "12px",
              borderRadius: "6px",
              cursor: "pointer",
              left: "center",
              backgroundColor: isSelected ? "#5cac49ff" : "#f0f0f0",
              color: isSelected ? "#fff" : "#333",
              fontWeight: isSelected ? "600" : "400",
              textAlign: "center",
              minWidth: "50px",
            }}
          >
            {month}
          </div>
        );
      })}
    </div>
  );
};

const CMSLobbyGraph = ({ data = [] }) => {
  const [selectedCell, setSelectedCell] = useState({
    row: "",
    column: "",
    value: 0,
  });
  const [matrixTwo, setMatrixTwo] = useState(null);

  const rows = useMemo(() => {
    return Array.isArray(data) ? data : [];
  }, [data]);

  // Extract months
  const months = useMemo(() => {
    const s = new Set();
    rows.forEach((d) => {
      if (d && d.cms_year != null && d.cms_month != null) {
        s.add(`${d.cms_year}-${d.cms_month}`);
      }
    });
    return Array.from(s).sort();
  }, [rows]);

  const [selectedMonths, setSelectedMonths] = useState(
    months.length ? months : []
  );

  const handleClickMonth = (month) => {
    setSelectedMonths((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      return cur.includes(month)
        ? cur.filter((m) => m !== month)
        : [...cur, month];
    });
  };

  const filteredRows = useMemo(() => {
    if (!Array.isArray(selectedMonths) || selectedMonths.length === 0)
      return [];
    return rows.filter(
      (d) =>
        d &&
        d.cms_year != null &&
        d.cms_month != null &&
        d.wh_lobby &&
        d.wh_lobby !== "TOTAL" &&
        selectedMonths.includes(`${d.cms_year}-${d.cms_month}`)
    );
  }, [rows, selectedMonths]);

  // Build aggregated map per lobby **based on filteredRows**
  const aggregated = useMemo(() => {
    const map = {};
    filteredRows.forEach((row) => {
      const lobby = row && row.wh_lobby;
      if (!lobby) return;
      if (!map[lobby]) {
        map[lobby] = {
          wh_less_than_equals_4hr: 0,
          wh_4_to_6_hr: 0,
          wh_6_to_8_hr: 0,
          wh_8_to_9_hr: 0,
          wh_9_to10_hr: 0,
          wh_10_to_11_hr: 0,
          wh_11_to_12_hr: 0,
          wh_12_to_14_hr: 0,
          wh_more_than_14hr: 0,
        };
      }

      map[lobby].wh_less_than_equals_4hr += toNum(row.wh_less_than_equals_4hr);
      map[lobby].wh_4_to_6_hr += toNum(row.wh_4_to_6_hr);
      map[lobby].wh_6_to_8_hr += toNum(row.wh_6_to_8_hr);
      map[lobby].wh_8_to_9_hr += toNum(row.wh_8_to_9_hr);
      map[lobby].wh_9_to10_hr += toNum(row.wh_9_to10_hr);
      map[lobby].wh_10_to_11_hr += toNum(row.wh_10_to_11_hr);
      map[lobby].wh_11_to_12_hr += toNum(row.wh_11_to_12_hr);
      map[lobby].wh_12_to_14_hr += toNum(row.wh_12_to_14_hr);
      map[lobby].wh_more_than_14hr += toNum(row.wh_more_than_14hr);
    });
    return map;
  }, [filteredRows]);

  // All lobbies to display on Y axis
  const lobbyList = useMemo(() => {
    const keys = Object.keys(aggregated || {});
    if (keys.length > 0) return keys.sort();
    // using unique lobbies from the full data (excluding TOTAL)
    const s = new Set();
    rows.forEach((d) => {
      if (d && d.wh_lobby && d.wh_lobby !== "TOTAL") s.add(d.wh_lobby);
    });
    return Array.from(s).sort();
  }, [aggregated, rows]);

  const matrix = useMemo(() => {
    if (!Array.isArray(lobbyList) || lobbyList.length === 0) return [];

    if (!filteredRows.length) {
      return lobbyList.map(() =>
        Array.from({ length: HOUR_LABELS.length }, () => [])
      );
    }

    return lobbyList.map((lobby) => {
      const r = aggregated[lobby] || {};
      return [
        toNum(r.wh_less_than_equals_4hr),
        toNum(r.wh_4_to_6_hr),
        toNum(r.wh_6_to_8_hr),
        toNum(r.wh_8_to_9_hr) + toNum(r.wh_9_to10_hr),
        toNum(r.wh_10_to_11_hr) + toNum(r.wh_11_to_12_hr),
        toNum(r.wh_12_to_14_hr),
        toNum(r.wh_more_than_14hr),
      ];
    });
  }, [lobbyList, filteredRows, aggregated]);

  // compute max for visualMap
  const maxValue = useMemo(() => {
    const flat = (matrix || []).flat();
    const max = flat.length
      ? Math.max(...flat.map((v) => (Number.isFinite(v) ? v : 0)))
      : 0;
    return max <= 0 ? 1 : max;
  }, [matrix]);

  useEffect(() => {
    const filterData = filteredRows.filter(
      (f) => f.wh_lobby === selectedCell.row
    );
    const months = [...new Set(filterData.map((o) => o.cms_month))];
    let tempMatrix = filterData.map((o) => {
      if (selectedCell.column === "8–10 hrs") {
        return [o["wh_8_to_9_hr"] + o["wh_9_to10_hr"]];
      } else if (selectedCell.column === "10–12 hrs") {
        return [o["wh_10_to_11_hr"] + o["wh_11_to_12_hr"]];
      } else {
        return [o[keyMap[selectedCell.column]]];
      }
    });
    let arrayOfMax = filterData
      .map((o) => {
        if (selectedCell.column === "8–10 hrs") {
          return o["wh_8_to_9_hr"] + o["wh_9_to10_hr"];
        } else if (selectedCell.column === "10–12 hrs") {
          return o["wh_10_to_11_hr"] + o["wh_11_to_12_hr"];
        } else {
          return o[keyMap[selectedCell.column]];
        }
      })
      .sort();

    setMatrixTwo({
      row: months,
      col: [selectedCell.column],
      matrix: tempMatrix,
      max: arrayOfMax[0],
      min: arrayOfMax[arrayOfMax.length - 1],
    });
  }, [selectedCell, filteredRows]);
  if (!lobbyList.length) return <p>No lobbies found</p>;
  return (
    <div className="newUiBody ">
      <div className="row">
        <div className="bg-dark text-white ps-1 p-1 d-flex align-items-center justify-content-between">
          <h5>Crew Lobby-wise HeatMap Chart</h5>
        </div>
        <div className=" ">
          <div className="row">
            <div className={`col-md-10 position-relative`}>
              <div
                className=" position-absolute top-0 start-50 translate-middle-x"
                style={{ zIndex: 3 }}
              >
                <MonthLegend
                  months={months}
                  selectedMonths={selectedMonths}
                  onClickMonth={handleClickMonth}
                />
              </div>{" "}
              <HeatmapChart
                setSelectedCell={setSelectedCell}
                rowsData={lobbyList}
                colsData={HOUR_LABELS}
                matrix={matrix}
                widthOfTheChart={"95%"}
                maxValue={maxValue}
                control={false}
                rangeBarLength={250}
              />
            </div>
            {matrixTwo?.matrix && (
              <div className={`col-md-2`}>
                <div className="m-1">
                  <HeatmapChart
                    title={`${selectedCell.row} // ${selectedCell.column}`}
                    rowsData={matrixTwo.row}
                    colsData={matrixTwo.col}
                    minValue={matrixTwo.min}
                    control={false}
                    widthOfTheChart={"50%"}
                    matrix={matrixTwo.matrix}
                    maxValue={matrixTwo.max}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMSLobbyGraph;
